import React, {useEffect, useRef, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {Container, Box, Stack} from '../../vanilla'
import {Breadcrumbs, Crumb} from '../../components/breadcrumbs'
import {SlotItem, SlotContentItem} from '../../types/cms'
import {ContentBody} from 'dc-delivery-sdk-js'
import {Seo} from '../../components/seo'
import {usePageDataTracking} from '../../analytics/page-tracking'
import {useCategoryStore} from '../../store/hooks/useStore'
import {getBottomSlot} from './partials/display-slots'
import { useIntersectionObserver } from '../../hooks/use-intersection-observer'
import { useLocation } from 'react-router-dom'

export interface CategoryPageProps {
  children: React.ReactNode
  slots?: SlotContentItem
  seo?: {
    pageTitle: string
    pageDescription: string
  }
  categoryId?: string
  name?: string
  categoryTree: Crumb[]
}

export interface SlotContentType extends ContentBody {
  default: SlotItem[]
}

const CategoryPage = observer(
  ({children, slots, seo, categoryId, categoryTree, name}: CategoryPageProps) => {

    const [bottomSlot, setBottomSlot] = useState(null)

    const location = useLocation()
    const currentContentId = `${location.pathname}?${location.search}`

    // For Lazy Loading the bottom slot...
    // IntersectionObserver occaisionally carries a stale state
    // accross page transitions - The below timeout
    // gives the page transition time to take place before setting "scrollObserverReady"
    const scrollObserverReady = useRef(false)

    useEffect(() => {
      scrollObserverReady.current = false
      const timeout = setTimeout(() => {
        scrollObserverReady.current = true
      }, 50)

      return () => {
        clearTimeout(timeout)
        scrollObserverReady.current = false
      }

    }, [currentContentId])

    const ref = useRef(null)
    // Detect if slot is within the viewport
    // Add a 300px offset as a buffer to preload just before the slot enters the screen
    // Helps avoid visible loading & layout shifting
    const isOnScreen = useIntersectionObserver(ref, {
      useOnce: true,
      rootMargin: '0px 0px 300px 0px',
    })

    useEffect(() => {
      if (scrollObserverReady.current && isOnScreen && slots) {
        setBottomSlot(getBottomSlot(slots))
      }
    }, [isOnScreen, slots, scrollObserverReady.current])

    const sendPageData = usePageDataTracking()

    let pageType = ''
    let pageCategory = ''
    let pageSubCategory = ''

    const {categories, status} = useCategoryStore()

    useEffect(() => {
      if (status !== 'done') {
        return
      }

      const category = categoryId && categoryId in categories ? categories[categoryId] : undefined

      if (category) {
        pageType = 'plp'
        const {isTopLevelCategory, parentCategoryTree, name} = category

        if (!isTopLevelCategory) {
          pageCategory = parentCategoryTree?.[0].name || ''
          pageSubCategory = name || ''
        } else {
          pageCategory = name || ''
        }
      }
      sendPageData(pageType, pageCategory, pageSubCategory)
    }, [status])

    return (
      <Container position="relative" paddingX="0px" paddingBottom="60px">
        <Seo
          title={seo?.pageTitle || `${name} | Iceland Foods`}
          description={seo?.pageDescription}
        />
        <Container top="0px" paddingX="0px" position="relative">
          <Box paddingY="12px">
            <Breadcrumbs breadcrumbs={categoryTree} categoryId={categoryId} />
          </Box>
        </Container>
        <>
          {children}
          <Stack ref={ref} key={currentContentId}>
            {bottomSlot}
          </Stack>
        </>
      </Container>
    )
  },
)

export default CategoryPage
